import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../common'; // Only Button is likely from common
import { toast } from 'react-toastify';

const NewEmployeeModal = ({ isOpen, onClose, onEmployeeCreated }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', // Added password field
    phone: '', country: 'Norway', hire_date: '',
    hourly_rate: '', role_id: null, status: 'Active', notes: '', address: '',
    bank_account: '', university: '', study_program: '', smtp_profile_id: null,
    email_signature: '',
    sendInvite: false // New field to track if invite should be sent
  });

  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [smtpProfiles, setSmtpProfiles] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: '',
    isValid: false,
    color: 'gray'
  });
  const API_BASE_URL = '/api';

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '', email: '', password: '', // Added password field
        phone: '', country: 'Norway', hire_date: '',
        hourly_rate: '', role_id: null, status: 'Active', notes: '', address: '',
        bank_account: '', university: '', study_program: '', smtp_profile_id: null,
        email_signature: '',
        sendInvite: false
      });
      setError('');
      fetchSmtpProfiles();
      fetchAvailableRoles();
    }
  }, [isOpen]);

  const fetchSmtpProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const res = await axios.get(`${API_BASE_URL}/settings/smtp-profiles`, config);
      setSmtpProfiles(res.data || []);
    } catch (err) {
      console.error('Error fetching SMTP profiles:', err);
      toast.warn('Could not load SMTP profiles.');
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const res = await axios.get(`${API_BASE_URL}/roles`, config);
      setAvailableRoles(res.data || []);
      const tutorRole = res.data.find(role => role.name === 'tutor');
      if (tutorRole) {
          setFormData(prev => ({ ...prev, role_id: tutorRole.id }));
      }
    } catch (err) {
      console.error('Error fetching available roles:', err);
      setError('Failed to load available roles.');
      toast.error('Failed to load available roles.');
    }
  };

  // Password validation requirements
  const minLength = 8;
  const hasUppercase = /[A-Z]/.test(formData.password);
  const hasLowercase = /[a-z]/.test(formData.password);
  const hasNumbers = /\d/.test(formData.password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
  
  // Update password strength when password changes
  useEffect(() => {
    if (!formData.sendInvite) { // Only validate if we're not sending an invite
      validatePassword(formData.password);
    }
  }, [formData.password, formData.sendInvite]);
  
  // Password validation function
  const validatePassword = (password) => {
    // Empty password
    if (!password) {
      setPasswordStrength({
        score: 0,
        message: '',
        isValid: false,
        color: 'gray'
      });
      return;
    }
    
    // Calculate password strength
    let score = 0;
    const checks = [
      password.length >= minLength,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    ];
    
    score = checks.filter(Boolean).length;
    
    // Set password strength message and color
    let message = '';
    let color = '';
    let isValid = false;
    
    // Check if we have the minimum requirements (8 chars, uppercase, lowercase, number)
    const hasMinRequirements = 
      password.length >= minLength && 
      /[A-Z]/.test(password) && 
      /[a-z]/.test(password) && 
      /\d/.test(password);
    
    if (score <= 2) {
      message = 'Weak password';
      color = 'red';
    } else if (score === 3) {
      // Check if the 3 requirements include our minimum set
      if (hasMinRequirements) {
        message = 'Good password';
        color = 'blue';
        isValid = true;
      } else {
        message = 'Fair password';
        color = 'orange';
      }
    } else if (score === 4) {
      message = 'Good password';
      color = 'blue';
      isValid = true;
    } else if (score === 5) {
      message = 'Strong password';
      color = 'green';
      isValid = true;
    }
    
    setPasswordStrength({ score, message, isValid, color });
  };

  useEffect(() => {
    const inputElement = document.getElementById('hourly_rate_new');
    if (inputElement) {
      inputElement.placeholder = `0.00 ${
        formData.country === 'Norway' ? 'NOK' :
        formData.country === 'Sweden' ? 'SEK' :
        formData.country === 'Germany' ? 'EUR' : ''
      }`;
    }
  }, [formData.country]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = 
      name === 'role_id' ? (value ? parseInt(value, 10) : null) : 
      type === 'checkbox' ? checked : 
      value;
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (formData.role_id === null || formData.role_id === undefined) {
        setError('Please select a role for the employee.');
        setLoading(false);
        toast.error('Role is required.');
        return;
    }
    
    // If sending invite, password is not required
    if (!formData.sendInvite) {
      if (!formData.password) { 
        setError('Password is required when not sending an invitation.');
        setLoading(false);
        toast.error('Password is required.');
        return;
      }
      
      // Validate password strength when setting password manually
      if (!passwordStrength.isValid) {
        setError('Please use a stronger password. Your password must be at least 8 characters and include uppercase letters, lowercase letters, and numbers. Special characters are recommended for increased security.');
        setLoading(false);
        toast.error('Password does not meet security requirements.');
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Content-Type': 'application/json', 'x-auth-token': token } };
      const dataToSubmit = {
        ...formData,
        hourly_rate: formData.hourly_rate === '' ? null : parseFloat(formData.hourly_rate),
        hire_date: formData.hire_date === '' ? null : formData.hire_date,
        smtp_profile_id: formData.smtp_profile_id === '' ? null : formData.smtp_profile_id
      };

      // Find the role name from the selected role_id
      const selectedRole = availableRoles.find(r => r.id === formData.role_id);
      const roleName = selectedRole ? selectedRole.name : null;

      if (!roleName) {
        setError('Selected role name could not be found. Please try again.');
        setLoading(false);
        toast.error('Selected role name could not be found.');
        return;
      }
      
      // Add role name and remove role_id from the data to submit
      dataToSubmit.role = roleName;
      delete dataToSubmit.role_id;
      
      // If sending invite, include sendInvite flag
      if (formData.sendInvite) {
        dataToSubmit.sendInvite = true;
        // Can remove password if it was entered but we're sending an invite
        delete dataToSubmit.password;
      }

      // Use the register endpoint
      const res = await axios.post(`${API_BASE_URL}/auth/register`, dataToSubmit, config);
      
      const successMessage = formData.sendInvite
        ? `Invitation email sent to ${dataToSubmit.name} (${dataToSubmit.email})`
        : `Employee "${dataToSubmit.name}" created successfully. Login required for the new user.`;
      
      toast.success(successMessage);
      onEmployeeCreated(); // Signal creation, parent component should refresh list
      onClose();
    } catch (err) {
      console.error('Error creating employee:', err);
      const errorMsg = err.response?.data?.message || 'Failed to create employee';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const currentRoleName = availableRoles.find(r => r.id === formData.role_id)?.name;
  // Define Tailwind classes for reuse
  const inputClass = "mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    // Modal container
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4" tabIndex="-1">
      {/* Modal content */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Modal header */}
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-xl font-semibold text-gray-800">Add New User</h3>
          <Button onClick={onClose} variant="ghost" className="text-gray-500 hover:text-gray-800">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </Button>
        </div>
        {/* Modal body */}
        <form onSubmit={handleSubmit} id="new-employee-form" className="p-6 overflow-y-auto flex-grow">
          {error && <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="md:col-span-2 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div><label htmlFor="name_new" className={labelClass}>Name *</label><input type="text" id="name_new" name="name" value={formData.name} onChange={handleChange} required className={inputClass} /></div>
                <div><label htmlFor="email_new" className={labelClass}>Email *</label><input type="email" id="email_new" name="email" value={formData.email} onChange={handleChange} required className={inputClass} /></div>
                <div>
                  <label htmlFor="role_id_new" className={labelClass}>Role *</label>
                  <select name="role_id" id="role_id_new" value={formData.role_id || ''} onChange={handleChange} required className={inputClass} disabled={availableRoles.length === 0}>
                    <option value="" disabled>-- Select Role --</option>
                    {availableRoles.map(role => <option key={role.id} value={role.id}>{role.name} {role.is_system_role ? '(System)' : ''}</option>)}
                  </select>
                  {availableRoles.length === 0 && !error && <p className="text-xs text-gray-500 mt-1">Loading roles...</p>}
                </div>
              </div>
              
              {/* Password section with the invite option */}
              <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                <div className="flex items-center mb-3">
                  <input 
                    type="checkbox" 
                    id="send_invite_new" 
                    name="sendInvite" 
                    checked={formData.sendInvite} 
                    onChange={handleChange} 
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" 
                  />
                  <label htmlFor="send_invite_new" className="ml-2 text-sm font-medium text-blue-800">
                    Send email invitation (user will set their own password)
                  </label>
                </div>
                
                {!formData.sendInvite && (
                  <div>
                    <label htmlFor="password_new" className={labelClass}>Password *</label>
                    <input 
                      type="password" 
                      id="password_new" 
                      name="password" 
                      value={formData.password} 
                      onChange={handleChange} 
                      required={!formData.sendInvite} 
                      className={`${inputClass} ${formData.password ? `border-${passwordStrength.color}-500` : ''}`} 
                    />
                    {formData.password && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-medium text-${passwordStrength.color}-500`}>
                            {passwordStrength.message}
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-${passwordStrength.color}-500`} 
                            style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                          />
                        </div>
                        <ul className="mt-2 text-xs text-gray-600 space-y-1">
                          <li className={`flex items-center ${formData.password.length >= minLength ? 'text-green-500' : ''}`}>
                            <span>{formData.password.length >= minLength ? '✓' : '○'}</span>
                            <span className="ml-1">Minimum 8 characters</span>
                          </li>
                          <li className={`flex items-center ${hasUppercase ? 'text-green-500' : ''}`}>
                            <span>{hasUppercase ? '✓' : '○'}</span>
                            <span className="ml-1">At least one uppercase letter</span>
                          </li>
                          <li className={`flex items-center ${hasLowercase ? 'text-green-500' : ''}`}>
                            <span>{hasLowercase ? '✓' : '○'}</span>
                            <span className="ml-1">At least one lowercase letter</span>
                          </li>
                          <li className={`flex items-center ${hasNumbers ? 'text-green-500' : ''}`}>
                            <span>{hasNumbers ? '✓' : '○'}</span>
                            <span className="ml-1">At least one number</span>
                          </li>
                          <li className={`flex items-center ${hasSpecialChar ? 'text-green-500' : ''}`}>
                            <span>{hasSpecialChar ? '✓' : '○'}</span>
                            <span className="ml-1">At least one special character (recommended)</span>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {formData.sendInvite && (
                  <p className="text-sm text-blue-700">
                    An invitation email will be sent to {formData.email || 'the user'} with instructions to set their password.
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label htmlFor="phone_new" className={labelClass}>Phone</label><input type="text" id="phone_new" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} /></div>
                <div><label htmlFor="country_new" className={labelClass}>Country</label><input type="text" id="country_new" name="country" value={formData.country} onChange={handleChange} className={inputClass} /></div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label htmlFor="hire_date_new" className={labelClass}>Hire Date</label><input type="date" id="hire_date_new" name="hire_date" value={formData.hire_date} onChange={handleChange} className={inputClass} /></div>
                <div>
                  <label htmlFor="status_new" className={labelClass}>Status</label>
                  <select id="status_new" name="status" value={formData.status} onChange={handleChange} className={inputClass}>
                    <option value="Active">Active</option><option value="On Leave">On Leave</option><option value="Terminated">Terminated</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="hourly_rate_new" className={labelClass}>Hourly Rate ({formData.country === 'Norway' ? 'NOK' : formData.country === 'Sweden' ? 'SEK' : formData.country === 'Germany' ? 'EUR' : ''})</label>
                  <input type="number" id="hourly_rate_new" name="hourly_rate" value={formData.hourly_rate} onChange={handleChange} step="0.01" min="0" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="bank_account_new" className={labelClass}>Bank Account Number</label>
                  <input type="text" id="bank_account_new" name="bank_account" value={formData.bank_account} onChange={handleChange} className={inputClass} />
                </div>
              </div>
              
              {/* Tutor-specific fields */}
              {currentRoleName === 'tutor' && (
                <div className="bg-blue-50 p-4 rounded-md border border-blue-100 mt-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-3">Tutor Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><label htmlFor="university_new" className={labelClass}>University</label><input type="text" id="university_new" name="university" value={formData.university} onChange={handleChange} className={inputClass} /></div>
                    <div><label htmlFor="study_program_new" className={labelClass}>Study Program</label><input type="text" id="study_program_new" name="study_program" value={formData.study_program} onChange={handleChange} className={inputClass} /></div>
                  </div>
                </div>
              )}
              
              {/* Address and Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label htmlFor="address_new" className={labelClass}>Address</label><textarea id="address_new" name="address" value={formData.address} onChange={handleChange} rows={3} className={inputClass}></textarea></div>
                <div><label htmlFor="notes_new" className={labelClass}>Notes</label><textarea id="notes_new" name="notes" value={formData.notes} onChange={handleChange} rows={3} className={inputClass}></textarea></div>
              </div>
            </div>
            
            {/* Right Column - Email Settings */}
            <div className="md:col-span-1">
              {(currentRoleName === 'admin' || currentRoleName === 'sales' || currentRoleName === 'superuser') ? (
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200 h-full flex flex-col">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Email Settings</h4>
                  <div className="mb-4">
                    <label htmlFor="smtp_profile_id_new" className={labelClass}>Email SMTP Profile</label>
                    <select name="smtp_profile_id" id="smtp_profile_id_new" value={formData.smtp_profile_id || ''} onChange={handleChange} className={inputClass}>
                      <option value="">Use Default Profile</option>
                      {smtpProfiles.map(profile => <option key={profile.id} value={profile.id}>{profile.name} ({profile.from_email})</option>)}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Profile for sending emails.</p>
                  </div>
                  <div className="flex-grow flex flex-col">
                    <label htmlFor="email_signature_new" className={labelClass}>Email Signature (HTML)</label>
                    <textarea name="email_signature" id="email_signature_new" value={formData.email_signature || ''} onChange={handleChange} rows={6} className={`${inputClass} font-mono text-sm flex-grow`} placeholder="<b>Your Name</b><br>Title<br>Company"></textarea>
                  </div>
                  {formData.email_signature && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                      <div className="border border-gray-300 rounded-md p-2 bg-white text-sm" dangerouslySetInnerHTML={{ __html: formData.email_signature }} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}></div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-100 p-4 rounded-md border border-gray-200 text-center flex items-center justify-center h-full"><p className="text-sm text-gray-500 italic">Email settings available for Admin, Sales, Superuser roles.</p></div>
              )}
            </div>
          </div>
        </form>
        
        {/* Modal Footer */}
        <div className="p-4 border-t flex justify-end space-x-3 sticky bottom-0 bg-gray-50 z-10">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            type="button" 
            onClick={handleSubmit} 
            variant="primary" 
            disabled={loading} 
            isLoading={loading}
          >
            {loading ? 'Saving...' : formData.sendInvite ? 'Create & Invite User' : 'Create User'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewEmployeeModal;
